import {Box, Button, HStack, Progress, Text, VStack} from '@chakra-ui/react';
import {TablerSearch} from '../../icons';

// Shared empty/status panel used by every patient picker that consumes
// `useEhrPatientSearch`. Centralises the wording + layout so that when
// the deep-search UX evolves (slow copy, unavailable copy, manual button
// placement) we only change it in one place.
export default function EhrDeepSearchStatus({
  isDeepSearching = false,
  deepSearchSlow = false,
  deepSearchUnavailable = false,
  canTriggerManualDeepSearch = false,
  onTriggerDeepSearch,
  emptyStateText = 'No patients found',
  // Optional compact layout for narrow containers (e.g. sidebars).
  variant = 'default',
  // When provided, a "Can't find them?" footer is rendered below whatever
  // primary state is shown. Callers use this for non-empty lists.
  showFooter = false,
}: Record<string, any>) {
  const subtle = 'gray.500';

  const footer =
    showFooter && canTriggerManualDeepSearch && onTriggerDeepSearch ? (
      <HStack
        justify="center"
        spacing={1.5}
        pt={2}
        color={subtle}
        fontSize="xs"
      >
        <Text>Not who you're looking for?</Text>
        <Button
          variant="link"
          size="xs"
          colorScheme="opalite"
          leftIcon={<TablerSearch />}
          onClick={onTriggerDeepSearch}
        >
          Search full EHR by patient name
        </Button>
      </HStack>
    ) : null;

  if (isDeepSearching) {
    return (
      <VStack spacing={3} w="100%" maxW="320px">
        <Text color={subtle} fontSize="md">
          {deepSearchSlow
            ? 'Still searching full EHR — this can take up to ~15 seconds.'
            : 'Searching full EHR… this may take several seconds.'}
        </Text>
        <Progress
          size="xs"
          w="100%"
          isIndeterminate
          colorScheme="opalite"
          borderRadius="full"
        />
        {footer}
      </VStack>
    );
  }

  if (deepSearchUnavailable) {
    return (
      <VStack spacing={2} maxW="320px">
        <Text color={subtle} fontSize="md" textAlign="center">
          Deep search is available weekdays 7am–8pm Eastern.
        </Text>
        {footer}
      </VStack>
    );
  }

  if (canTriggerManualDeepSearch && onTriggerDeepSearch) {
    return (
      <VStack spacing={variant === 'compact' ? 2 : 3} maxW="320px">
        <Text
          color={subtle}
          fontSize="md"
          textAlign="center"
          fontWeight="normal"
        >
          {emptyStateText}
        </Text>
        <Button
          size="md"
          variant="outline"
          colorScheme="opalite"
          leftIcon={<TablerSearch />}
          onClick={onTriggerDeepSearch}
        >
          Search full EHR by patient name
        </Button>
        {variant !== 'compact' && (
          <Text color={subtle} fontSize="sm" textAlign="center">
            Searches by last name. Use “Last, First” for an exact match (e.g.
            Smith, Jane).
          </Text>
        )}
      </VStack>
    );
  }

  // Plain empty state (no manual trigger available — e.g. local-only picker).
  return (
    <Box>
      <Text color={subtle} fontSize="md" textAlign="center">
        {emptyStateText}
      </Text>
    </Box>
  );
}
